import { useState } from 'react';
import { apiFetch, useApi } from '../hooks/useApi.js';

// ── Shared UI primitives (kept local — same look as Clients.jsx / Salsa.jsx) ──
function Btn({ children, variant = 'primary', size = 'md', ...props }) {
  const base = { borderRadius: 'var(--radius)', fontWeight: 600, cursor: 'pointer', border: 'none', fontSize: size === 'sm' ? 12 : 14, padding: size === 'sm' ? '5px 12px' : '9px 18px', opacity: props.disabled ? 0.5 : 1, transition: 'opacity .15s' };
  const v = { primary: { background: 'var(--pld-green)', color: '#fff' }, ghost: { background: 'var(--surface-2)', color: 'var(--text)', border: '1px solid var(--border)' }, danger: { background: '#dc2626', color: '#fff' } };
  return <button {...props} style={{ ...base, ...v[variant], ...props.style }}>{children}</button>;
}

function Modal({ title, onClose, width = 480, children }) {
  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 300, background: 'rgba(0,0,0,.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }} onClick={onClose}>
      <div style={{ background: 'var(--surface)', borderRadius: 'var(--radius-lg)', boxShadow: 'var(--shadow-lg)', width: '100%', maxWidth: width, maxHeight: '90vh', overflow: 'auto' }} onClick={e => e.stopPropagation()}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '18px 24px', borderBottom: '1px solid var(--border)' }}>
          <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: 'var(--text)' }}>{title}</h3>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 20, color: 'var(--text-muted)', lineHeight: 1 }}>×</button>
        </div>
        <div style={{ padding: 24 }}>{children}</div>
      </div>
    </div>
  );
}

function Field({ label, hint, children }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
      <label style={{ fontSize: 13, fontWeight: 500, color: 'var(--text)' }}>{label}</label>
      {children}
      {hint && <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>{hint}</span>}
    </div>
  );
}

function Input(props) {
  return <input {...props} style={{ padding: '8px 11px', borderRadius: 'var(--radius)', border: '1px solid var(--border)', fontSize: 14, background: 'var(--surface-2)', width: '100%', boxSizing: 'border-box', color: 'var(--text)', ...props.style }} />;
}

function SelectEl({ children, ...props }) {
  return <select {...props} style={{ padding: '8px 11px', borderRadius: 'var(--radius)', border: '1px solid var(--border)', fontSize: 14, background: 'var(--surface-2)', width: '100%', boxSizing: 'border-box', color: 'var(--text)', ...props.style }}>{children}</select>;
}

function Textarea(props) {
  return <textarea {...props} style={{ padding: '8px 11px', borderRadius: 'var(--radius)', border: '1px solid var(--border)', fontSize: 14, background: 'var(--surface-2)', width: '100%', boxSizing: 'border-box', color: 'var(--text)', resize: 'vertical', minHeight: 60, ...props.style }} />;
}

const WEEKDAY_LABELS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

function fmtWeekRange(start, end) {
  const s = new Date(start), e = new Date(end);
  const opts = { day: 'numeric', month: 'short' };
  return `${s.toLocaleDateString('en-GB', opts)} – ${e.toLocaleDateString('en-GB', opts)}`;
}

function shiftWeek(dateStr, deltaDays) {
  const d = new Date(dateStr);
  d.setUTCDate(d.getUTCDate() + deltaDays);
  return d.toISOString().slice(0, 10);
}

// ── Add / edit task template modal ────────────────────────────────────────────
function TaskTemplateModal({ initial, users, onClose, onSaved }) {
  const isEdit = !!initial;
  const [title, setTitle] = useState(initial?.title || '');
  const [description, setDescription] = useState(initial?.description || '');
  const [frequency, setFrequency] = useState(initial?.frequency || 'daily');
  const [assignedTo, setAssignedTo] = useState(initial?.assigned_to || '');
  const [daysOfWeek, setDaysOfWeek] = useState(() => new Set(initial?.days_of_week || (initial?.frequency === 'weekly' ? [1] : [1, 2, 3, 4, 5])));
  const [dayOfMonth, setDayOfMonth] = useState(initial?.day_of_month || 1);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  function toggleDay(iso) {
    setDaysOfWeek(prev => {
      const next = new Set(frequency === 'weekly' ? [] : prev);
      if (frequency === 'weekly') { next.add(iso); return next; }
      if (next.has(iso)) next.delete(iso); else next.add(iso);
      return next;
    });
  }

  async function save(e) {
    e.preventDefault();
    if (!title.trim()) return setError('Title is required');
    setSaving(true); setError('');
    try {
      const body = {
        title: title.trim(),
        description: description.trim() || null,
        frequency,
        assigned_to: assignedTo || null,
        days_of_week: frequency === 'monthly' ? undefined : [...daysOfWeek],
        day_of_month: frequency === 'monthly' ? Number(dayOfMonth) : undefined,
      };
      if (isEdit) {
        await apiFetch(`/staff-tasks/templates/${initial.id}`, { method: 'PATCH', body });
      } else {
        await apiFetch('/staff-tasks/templates', { method: 'POST', body });
      }
      onSaved();
    } catch (err) {
      setError(err.message || 'Failed to save task');
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal title={isEdit ? 'Edit Task' : 'New Task'} onClose={onClose}>
      <form onSubmit={save} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        <Field label="Title">
          <Input value={title} onChange={e => setTitle(e.target.value)} placeholder="e.g. Check bottling line CIP log" autoFocus />
        </Field>
        <Field label="Notes" hint="Optional — detail for whoever's doing it">
          <Textarea value={description} onChange={e => setDescription(e.target.value)} />
        </Field>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
          <Field label="Frequency">
            <SelectEl value={frequency} onChange={e => {
              const f = e.target.value;
              setFrequency(f);
              if (f === 'weekly') setDaysOfWeek(new Set([[...daysOfWeek][0] || 1]));
              if (f === 'daily' && daysOfWeek.size === 0) setDaysOfWeek(new Set([1, 2, 3, 4, 5]));
            }}>
              <option value="daily">Daily</option>
              <option value="weekly">Weekly</option>
              <option value="monthly">Monthly</option>
            </SelectEl>
          </Field>
          <Field label="Assigned to" hint="Leave blank for a shared/unassigned task">
            <SelectEl value={assignedTo} onChange={e => setAssignedTo(e.target.value)}>
              <option value="">— Unassigned —</option>
              {users.map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
            </SelectEl>
          </Field>
        </div>

        {frequency === 'monthly' ? (
          <Field label="Day of month" hint="Clamped to the last day in shorter months">
            <Input type="number" min={1} max={31} value={dayOfMonth} onChange={e => setDayOfMonth(e.target.value)} />
          </Field>
        ) : (
          <Field label={frequency === 'weekly' ? 'Due on' : 'Due on these days'}>
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
              {WEEKDAY_LABELS.map((label, idx) => {
                const iso = idx + 1;
                const active = daysOfWeek.has(iso);
                return (
                  <button key={isO} type="button" onClick={() => toggleDay(iso)}
                    style={{
                      padding: '6px 12px', borderRadius: 'var(--radius)', fontSize: 13, fontWeight: 600, cursor: 'pointer',
                      border: active ? '1px solid var(--pld-green)' : '1px solid var(--border)',
                      background: active ? 'var(--pld-green)' : 'var(--surface-2)',
                      color: active ? '#fff' : 'var(--text)',
                    }}>
                    {label}
                  </button>
                );
              })}
            </div>
          </Field>
        )}

        {error && <p style={{ margin: 0, color: '#dc2626', fontSize: 13 }}>{error}</p>}
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <Btn type="button" variant="ghost" onClick={onClose}>Cancel</Btn>
          <Btn type="submit" disabled={saving}>{saving ? 'Saving…' : isEdit ? 'Save Changes' : 'Add Task'}</Btn>
        </div>
      </form>
    </Modal>
  );
}

// ── Manage tasks modal  (list + deactivate/reactivate + edit) ────────────────
function ManageTasksModal({ users, onClose, onChanged }) {
  const [includeInactive, setIncludeInactive] = useState(false);
  const { data, loading, refetch } = useApi(`/staff-tasks/templates?include_inactive=${includeInactive}`, [includeInactive]);
  const [editing, setEditing] = useState(null);
  const [adding, setAdding] = useState(false);
  const templates = Array.isArray(data) ? data : [];

  async function toggleActive(t) {
    const action = t.is_active ? 'deactivate' : 'reactivate';
    await apiFetch(`/staff-tasks/templates/${t.id}/${action}`, { method: 'POST' });
    refetch();
    onChanged();
  }

  if (adding || editing) {
    return (
      <TaskTemplateModal
        initial={editing}
        users={users}
        onClose={() => { setAdding(false); setEditing(null); }}
        onSaved={() => { setAdding(false); setEditing(null); refetch(); onChanged(); }}
      />
    );
  }

  return (
    <Modal title="Manage Tasks" onClose={onClose} width={640}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, color: 'var(--text-muted)', cursor: 'pointer' }}>
            <input type="checkbox" checked={includeInactive} onChange={e => setIncludeInactive(e.target.checked)} />
            Show deactivated
          </label>
          <Btn size="sm" onClick={() => setAdding(true)}>+ New Task</Btn>
        </div>

        {loading ? (
          <p style={{ color: 'var(--text-muted)', fontSize: 14 }}>Loading…</p>
        ) : !templates.length ? (
          <p style={{ color: 'var(--text-muted)', fontSize: 14 }}>No tasks yet — add one to get started.</p>
        ) : (
          <div style={{ border: '1px solid var(--border)', borderRadius: 'var(--radius)', overflow: 'hidden' }}>
            {templates.map(t => (
              <div key={t.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px', borderBottom: '1px solid var(--border)', opacity: t.is_active ? 1 : 0.55 }}>
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 600, fontSize: 14, color: 'var(--text)' }}>{t.title}</div>
                  <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>
                    {t.frequency === 'monthly' ? `Monthly, day ${t.day_of_month}` : t.frequency === 'weekly' ? `Weekly — ${WEEKDAX_LABELS[(t.days_of_week?.[0] || 1) - 1]}` : `Daily — ${(t.days_of_week || []).map(d => WEEKDAY_LABELS[d - 1]).join(', ')}`}
                    {' · '}{t.assignee_name || 'Unassigned'}
                  </div>
                </div>
                <Btn size="sm" variant="ghost" onClick={() => setEditing(t)}>Edit</Btn>
                <Btn size="sm" variant={t.is_active ? 'danger' : 'ghost'} onClick={() => toggleActive(t)}>
                  {t.is_active ? 'Deactivate' : 'Reactivate'}
                </Btn>
              </div>
            ))}
          </div>
        )}
      </div>
    </Modal>
  );
}

// ── Week board grid ────────────────────────────────────────────
function Cell({ cell, onToggle }) {
  if (!cell.due) return <td style={{ padding: 8, textAlign: 'center' }} />;
  return (
    <td style={{ padding: 8, textAlign: 'center' }}>
      <button
        onClick={() => onToggle(!cell.completed)}
        title={cell.completed ? `Done${cell.completedByName ? ' by ' + cell.completedByName : ''}` : cell.overdue ? 'Overdue' : 'Not done yet'}
        style={{
          width: 28, height: 28, borderRadius: 7, cursor: 'pointer',
          border: cell.completed ? '1px solid #166534' : cell.overdue ? '1px solid #dc2626' : '1px solid var(--border)',
          background: cell.completed ? '#dcfce7' : cell.overdue ? '#fee2e2' : 'var(--surface-2)',
          color: cell.completed ? '#166534' : cell.overdue ? '#991b1b' : 'var(--text-muted)',
          fontSize: 15, fontWeight: 700, lineHeight: 1,
        }}
      >
        {cell.completed ? '✓' : cell.overdue ? '!' : ''}
      </button>
    </td>
  );
}

function WeekBoard({ board, onToggle }) {
  if (!board.tasks.length) {
    return <p style={{ padding: 24, textAlign: 'center', color: 'var(--text-muted)', fontSize: 14 }}>No daily or weekly tasks yet — add one with "Manage Tasks".</p>;
  }
  return (
    <div style={{ border: '1px solid var(--border)', borderRadius: 'var(--radius-lg)', overflow: 'auto' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
        <thead>
          <tr style={{ background: 'var(--surface-2)', borderBottom: '2px solid var(--border)' }}>
            <th style={{ padding: '10px 14px', textAlign: 'left', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.05em', color: 'var(--text-muted)' }}>Task</th>
            <th style={{ padding: '10px 14px', textAlign: 'left', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.05em', color: 'var(--text-muted)' }}>Assigned</th>
            {board.days.map((d, i) => (
              <th key={d.date} style={{ padding: '10px 8px', textAlign: 'center', fontSize: 11, fontWeight: 700, color: 'var(--text-muted)' }}>
                {WEEKDAY_LABELS[i]}<br />
                <span style={{ fontWeight: 400, fontSize: 10 }}>{new Date(d.date).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}</span>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {board.tasks.map(t => (
            <tr key={t.id} style={{ borderBottom: '1px solid var(--border)' }}>
              <td style={{ padding: '10px 14px', fontWeight: 600, color: 'var(--text)' }}>
                {t.title}
                <div style={{ fontSize: 11, color: 'var(--text-muted)', fontWeight: 400, textTransform: 'capitalize' }}>{t.frequency}</div>
              </td>
              <td style={{ padding: '10px 14px', color: 'var(--text-muted)' }}>{t.assigneeName || '—'}</td>
              {t.cells.map(cell => (
                <Cell key={cell.date} cell={cell} onToggle={(completed) => onToggle(t.id, cell.date, completed)} />
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function MonthlyTasks({ tasks, onToggle }) {
  if (!tasks.length) return null;
  return (
    <div style={{ marginTop: 20 }}>
      <h4 style={{ margin: '0 0 10px', fontSize: 13, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.06em', color: 'var(--text-muted)' }}>Monthly</h4>
      <div style={{ border: '1px solid var(--border)', borderRadius: 'var(--radius)', overflow: 'hidden' }}>
        {tasks.map(t => (
          <div key={t.id} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 14px', borderBottom: '1px solid var(--border)' }}>
            <button
              onClick={() => onToggle(t.id, t.dueDate, !t.completed)}
              style={{
                width: 26, height: 26, borderRadius: 7, cursor: 'pointer', flexShrink: 0,
                border: t.completed ? '1px solid #166534' : t.overdue ? '1px solid #dc2626' : '1px solid var(--border)',
                background: t.completed ? '#dcfce7' : t.overdue ? '#fee2e2' : 'var(--surface-2)',
                color: t.completed ? '#166534' : t.overdue ? '#991b1b' : 'var(--text-muted)',
                fontSize: 14, fontWeight: 700,
              }}
            >
              {t.completed ? '✓' : t.overdue ? '!' : ''}
            </button>
            <div style={{ flex: 1 }}>
              <div style={{ fontWeight: 600, fontSize: 14, color: 'var(--text)' }}>{t.title}</div>
              <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>
                Due {new Date(t.dueDate).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })} · {t.assigneeName || 'Unassigned'}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Page ──────────────────────────────────────────────────────────────────────
export default function StaffTasks() {
  const [weekOf, setWeekOf] = useState(null); // null = current week
  const [showManage, setShowManage] = useState(false);
  const { data: usersData } = useApi('/staff-tasks/assignable-users');
  const users = Array.isArray(usersData) ? usersData : [];

  const boardUrl = weekOf ? `/staff-tasks/board?week=${weekOf}` : '/staff-tasks/board';
  const { data: board, loading, refetch } = useApi(boardUrl, [boardUrl]);

  async function toggle(templateId, dueDate, completed) {
    await apiFetch('/staff-tasks/completions', { method: 'POST', body: { templateId, dueDate, completed } });
    refetch();
  }

  return (
    <div style={{ maxWidth: 1100, margin: '0 auto', padding: '0 8px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 24, flexWrap: 'wrap', gap: 12 }}>
        <div>
          <h1 style={{ margin: 0, fontSize: 22, fontWeight: 700, color: 'var(--text)' }}>Staff Tasks</h1>
          <p style={{ margin: '4px 0 0', color: 'var(--text-muted)', fontSize: 14 }}>The recurring task board — daily, weekly and monthly jobs, assigned by name</p>
        </div>
        <Btn onClick={() => setShowManage(true)}>Manage Tasks</Btn>
      </div>

      {board && (
        <>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
            <Btn size="sm" variant="ghost" onClick={() => setWeekOf(shiftWeek(board.weekStart, -7))}>← Previous week</Btn>
            <span style={{ fontSize: 14, fontWeight: 600, color: 'var(--text)' }}>{fmtWeekRange(board.weekStart, board.weekEnd)}</span>
            <Btn size="sm" variant="ghost" onClick={() => setWeekOf(shiftWeek(board.weekStart, 7))}>Next week →</Btn>
            {weekOf && <Btn size="sm" variant="ghost" onClick={() => setWeekOf(null)}>Today</Btn>}
          </div>

          {loading ? (
            <p style={{ color: 'var(--text-muted)', fontSize: 14 }}>Loading…</p>
          ) : (
            <>
              <WeekBoard board={board} onToggle={toggle} />
              <MonthlyTasks tasks={board.monthlyTasks} onToggle={toggle} />
            </>
          )}
        </>
      )}

      {showManage && (
        <ManageTasksModal users={users} onClose={() => setShowManage(false)} onChanged={refetch} />
      )}
    </div>
  );
}
